import type React from 'react';

import { InProgressIcon, StarFullIcon, WarningIcon } from '~/components/icons';
import type { IconProps } from '~/components/icons/types';
import colors from '~/theme/tailwind/colors';

import type { StpNotificationMetadata } from './types';

/** The three shapes an entry takes when it is worth more than a line of narration. */
export type AccentVariant = 'milestone' | 'pending' | 'error';

interface AccentConfig {
  label: string;
  Icon: React.FC<IconProps>;
  iconColor: string;
  /** Full literal class strings so Tailwind's content scan keeps them. */
  cardClassName: string;
  labelClassName: string;
}

export const ACCENT_CONFIG: Record<AccentVariant, AccentConfig> = {
  milestone: {
    label: 'Milestone',
    Icon: StarFullIcon,
    iconColor: colors.green[600],
    cardClassName: 'border-green-600 bg-green-100',
    labelClassName: 'text-green-700',
  },
  pending: {
    // No bulb icon exists in our set; InProgressIcon is the closest in-house option.
    label: 'Pending',
    Icon: InProgressIcon,
    iconColor: colors.blue[600],
    cardClassName: 'border-blue-300 bg-blue-100',
    labelClassName: 'text-blue-700',
  },
  error: {
    label: 'Error',
    Icon: WarningIcon,
    iconColor: colors.orange[700],
    cardClassName: 'border-orange-700 bg-orange-100',
    labelClassName: 'text-orange-700',
  },
};

/**
 * Which accent an entry earns, or `null` where it is plain narration.
 *
 * The precedence is the one `StpNotificationMetadata` documents: an error outranks a pending
 * outranks a milestone.
 */
export const getAccentVariant = (metadata?: StpNotificationMetadata): AccentVariant | null => {
  if (metadata?.is_error) {
    return 'error';
  }
  if (metadata?.is_pending) {
    return 'pending';
  }
  if (metadata?.is_milestone) {
    return 'milestone';
  }

  return null;
};
